import { Link, useParams } from 'react-router-dom';
import { getArticleById, getRelatedArticles, hotArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/base/ArticleCard';
import Breadcrumb from '@/components/base/Breadcrumb';
import StatusBadge from '@/components/base/StatusBadge';
import SeoMeta from '@/components/base/SeoMeta';
import JsonLd from '@/components/base/JsonLd';
import { getCategoryName } from '@/utils/content';

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const basePath = (__BASE_PATH__ || '/').replace(/\/$/, '');

  if (!article) {
    return (
      <section className="mx-auto flex w-full flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <SeoMeta
          title="案例不存在 - 真相回旋镖"
          description="你访问的案例可能已被合并或更新，请返回反转档案重新查找。"
          path="/archive"
        />
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-3xl text-foreground-400">
          <i className="ri-file-unknow-line" aria-hidden="true" />
        </span>
        <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">没有找到这条案例</h1>
        <p className="mt-3 max-w-md text-sm leading-relaxed text-foreground-600">
          它可能已经合并进其他案例，或者链接被改写过。你可以回到反转档案，按分类重新查找。
        </p>
        <Link
          to="/archive"
          className="mt-7 flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
        >
          <i className="ri-arrow-left-line" aria-hidden="true" />
          返回反转档案
        </Link>
      </section>
    );
  }

  const related = getRelatedArticles(article.id, 3);
  const categoryName = getCategoryName(article.category);

  return (
    <>
      <SeoMeta
        title={`${article.title} - 真相回旋镖`}
        description={article.summary}
        keywords={`${article.tags.join(',')},${categoryName},爆料反转`}
        path={`/article/${article.id}`}
        type="article"
        image={article.cover}
      />
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: article.title,
            description: article.summary,
            image: article.cover,
            datePublished: article.publishDate,
            dateModified: article.publishDate,
            author: { '@type': 'Organization', name: article.author },
            publisher: { '@type': 'Organization', name: '真相回旋镖' },
            articleSection: categoryName,
            keywords: article.tags.join(','),
            mainEntityOfPage: `${origin}${basePath}/article/${article.id}`,
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${origin}${basePath}/` },
              { '@type': 'ListItem', position: 2, name: '反转档案', item: `${origin}${basePath}/archive` },
              { '@type': 'ListItem', position: 3, name: article.title, item: `${origin}${basePath}/article/${article.id}` },
            ],
          },
        ]}
      />

      <div className="mx-auto grid w-full grid-cols-1 gap-10 px-4 py-10 md:px-6 md:py-14 lg:grid-cols-12 lg:gap-12">
        <article className="lg:col-span-8">
          <Breadcrumb
            items={[
              { label: '首页', to: '/' },
              { label: '反转档案', to: '/archive' },
              { label: categoryName, to: `/archive?category=${article.category}` },
            ]}
          />

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <Link
              to={`/archive?category=${article.category}`}
              className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium font-label text-secondary-800 transition-colors hover:bg-secondary-200"
            >
              {categoryName}
            </Link>
            <StatusBadge status={article.status} />
          </div>

          <h1 className="mt-4 font-heading text-2xl font-bold leading-snug text-foreground-950 sm:text-3xl md:text-4xl">
            {article.title}
          </h1>

          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-background-300 pb-5 text-xs font-label text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-line" aria-hidden="true" />
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line" aria-hidden="true" />
              {article.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-fire-line text-primary-500" aria-hidden="true" />
              热度 {article.heat}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-shield-check-line text-secondary-600" aria-hidden="true" />
              已标注信息来源
            </span>
          </div>

          <div className="mt-7 aspect-[16/9] w-full overflow-hidden rounded-lg border border-background-300 bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 配图`}
              title={`${article.title}｜黑料反转与辟谣澄清`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <p className="mt-7 rounded-lg border-l-4 border-primary-400 bg-primary-50/70 p-5 text-sm leading-relaxed text-foreground-700 md:text-base">
            {article.summary}
          </p>

          <div className="mt-9 flex flex-col gap-9">
            {article.sections.map((section) => (
              <section key={section.heading}>
                <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                  {section.heading}
                </h2>
                <div className="mt-4 flex flex-col gap-4">
                  {section.paragraphs.map((paragraph, index) => (
                    <p
                      key={`${section.heading}-${index}`}
                      className="text-sm leading-[1.9] text-foreground-700 md:text-base"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>
              </section>
            ))}
          </div>

          <section aria-labelledby="timeline-heading" className="mt-12 rounded-lg border border-background-300 bg-background-100 p-5 md:p-7">
            <h2 id="timeline-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              事件时间线
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              从爆料出现到结论公布，关键节点按时间顺序排列。
            </p>
            <ol className="mt-6 flex flex-col">
              {article.timeline.map((item, index) => (
                <li key={`${item.date}-${item.label}`} className="relative flex gap-4 pb-6 last:pb-0">
                  <div className="flex flex-col items-center">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-secondary-300 bg-background-50 text-[11px] font-semibold font-label text-secondary-700">
                      {item.date}
                    </span>
                    {index < article.timeline.length - 1 ? (
                      <span className="mt-1 w-px flex-1 bg-background-400" aria-hidden="true" />
                    ) : null}
                  </div>
                  <div className="pt-1">
                    <h3 className="font-heading text-base font-semibold text-foreground-950">
                      {item.label}
                    </h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          <section aria-labelledby="statement-heading" className="mt-8 rounded-lg border border-secondary-200 bg-secondary-50 p-5 md:p-7">
            <div className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                <i className="ri-double-quotes-l" aria-hidden="true" />
              </span>
              <h2 id="statement-heading" className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                当事方与相关机构回应
              </h2>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-foreground-800 md:text-base">
              {article.statement}
            </p>
            <p className="mt-3 text-xs font-label text-foreground-500">—— {article.statementSource}</p>
          </section>

          <div className="mt-8 flex flex-wrap items-center gap-2">
            <span className="text-xs font-label text-foreground-500">相关标签：</span>
            {article.tags.map((tag) => (
              <span
                key={tag}
                className="rounded-md bg-background-100 px-2.5 py-1 text-xs font-label text-foreground-700"
              >
                #{tag}
              </span>
            ))}
          </div>

          <p className="mt-8 rounded-lg bg-background-100 p-4 text-xs leading-relaxed text-foreground-500">
            说明：本案例为情景化示例整理，涉事人物均作匿名处理。上文所有结论均以可核实材料与公开说明为准，如有新的进展，页面将同步更新。
          </p>

          <section aria-labelledby="related-heading" className="mt-14">
            <h2 id="related-heading" className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              相关案例推荐
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((item) => (
                <ArticleCard key={item.id} article={item} />
              ))}
            </div>
          </section>
        </article>

        <aside className="lg:col-span-4">
          <div className="rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
            <div className="flex items-center gap-2 border-b border-background-200 pb-4">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary-100 text-primary-700">
                <i className="ri-fire-line" aria-hidden="true" />
              </span>
              <h2 className="font-heading text-lg font-bold text-foreground-950">热议榜</h2>
            </div>
            <div className="mt-5 flex flex-col gap-5">
              {hotArticles.slice(0, 5).map((item, index) => (
                <ArticleCard
                  key={item.id}
                  article={item}
                  variant="compact"
                  indexText={String(index + 1).padStart(2, '0')}
                />
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-lg border border-background-300 bg-background-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">按走向浏览</h2>
            <ul className="mt-4 flex flex-col gap-2">
              {categories.map((item) => (
                <li key={item.key}>
                  <Link
                    to={`/archive?category=${item.key}`}
                    className="flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-label text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-700"
                  >
                    <span className="flex items-center gap-2">
                      <i className={item.icon} aria-hidden="true" />
                      {item.name}
                    </span>
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-6 rounded-lg border border-primary-200 bg-primary-50 p-5 md:p-6">
            <h2 className="font-heading text-lg font-bold text-foreground-950">发现内容有误？</h2>
            <p className="mt-2 text-sm leading-relaxed text-foreground-600">
              附上原始链接或可核对材料发给我们，核实后会标注更正时间与更正内容。
            </p>
            <Link
              to="/about#contact"
              className="mt-4 flex items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-mail-send-line" aria-hidden="true" />
              提交更正
            </Link>
          </div>
        </aside>
      </div>
    </>
  );
}